/**
 * TYPES
 */
import * as Commands from 'src/aggregates/conversations/commands.d';
import {IConversationOnUploadProgress}
    from 'src/aggregates/conversations/commands.d';
import {IConversationOnUploadSuccess}
    from 'src/aggregates/conversations/commands.d';
import {IConversationRetryMessage}
    from 'src/aggregates/conversations/commands.d';
import {_names} from 'src/aggregates/conversations/commands.d';
import {AckTypes} from 'src/aggregates/conversations/message/messages.d';
import {ITemplateInput} from 'src/aggregates/conversations/queries.d';
import {IMessage} from 'src/aggregates/conversations/state.d';
import {IMessageOnProgress} from 'src/aggregates/conversations/state.d';
import {IMessageOnSuccess} from 'src/aggregates/conversations/state.d';
import {IMetricsFilters} from 'src/aggregates/conversations/state.d';
import {IQualityMetadata} from 'src/aggregates/conversations/state.d';
import {ICommandCreator} from 'src/aggregates/index.d';
import {_aggregates} from 'src/aggregates/index.d';
import {IFeedbackParams} from 'src/aggregates/utils/feedback';
import {ToastTypes} from 'src/aggregates/utils/feedback';
import {IMessage as IComponentMessage}
    from 'src/components/Shared/Message/index.d';


/**
 * CODE
 */

/**
 * I am a command creator for conversation add forwarding message.
 *
 * :param message: message to forward
 *
 * :returns: conversation add forwarding message command
 */
function addForwardingMessage (
    message: IComponentMessage,
): Commands.IConversationAddForwardingMessage
{
    return {
        _aggregate: _aggregates.CONVERSATIONS,
        _name: _names.CONVERSATION_ADD_FORWARDING_MESSAGE,
        _target: null,
        message,
    };
}


/**
 * I am a command creator for conversation close session.
 *
 * :returns: conversation close session command
 */
function closeSession (): Commands.IConversationCloseSession
{
    return {
        _aggregate: _aggregates.CONVERSATIONS,
        _name: _names.CONVERSATION_CLOSE_SESSION,
        _target: null,
    };
}


/**
 * I am a command creator for conversation create.
 *
 * :param account: account id
 * :param contact: contact value
 * :param subject: conversation subject
 * :param type: contact type
 *
 * :returns: conversation create command
 */
const create: ICommandCreator<Commands.IConversationCreate> = (
    account: number,
    contact: string,
    subject: string | null,
    type: number,
): Commands.IConversationCreate => ({
    _aggregate: _aggregates.CONVERSATIONS,
    _name: _names.CONVERSATION_CREATE,
    _target: null,
    account,
    contact,
    subject,
    type,
});


/**
 * I am a command creator for conversation ensure.
 *
 * :param conversation: conversation id
 *
 * :returns: conversation ensure command
 */
const ensure: ICommandCreator<Commands.IConversationEnsure> = (
    conversation: number,
): Commands.IConversationEnsure => ({
    _aggregate: _aggregates.CONVERSATIONS,
    _name: _names.CONVERSATION_ENSURE,
    _target: conversation,
});


/**
 * I am a command creator for conversation ensure ongoing.
 *
 * :param conversation: conversation id
 *
 * :returns: conversation ensure ongoing command
 */
const ensureOngoing: ICommandCreator<Commands.IConversationEnsureOngoing> = (
    conversation: number,
): Commands.IConversationEnsureOngoing => ({
    _aggregate: _aggregates.CONVERSATIONS,
    _name: _names.CONVERSATION_ENSURE_ONGOING,
    _target: conversation,
});


/**
 * I am a command creator for conversation fetch.
 *
 * :param id: conversation id
 *
 * :returns: conversation fetch command
 */
const fetch: ICommandCreator<Commands.IConversationFetch> = (
    id: number,
): Commands.IConversationFetch => ({
    _aggregate: _aggregates.CONVERSATIONS,
    _name: _names.CONVERSATION_FETCH,
    _target: id,
});


/**
 * I am a command creator for conversation fetch agents messages metrics.
 *
 * :param filters: metrics filters
 *
 * :returns: conversation fetch agents messages metrics command
 */
const fetchAgentsMessagesMetrics: ICommandCreator<
    Commands.IConversationFetchAgentsMessagesMetrics
> = (
    filters?: IMetricsFilters,
): Commands.IConversationFetchAgentsMessagesMetrics => ({
    _aggregate: _aggregates.CONVERSATIONS,
    _name: _names.CONVERSATION_FETCH_AGENTS_MESSAGES_METRICS,
    _target: null,
    filters,
});


/**
 * I am a command creator for conversation fetch by ids.
 *
 * :param conversations: list of conversation ids
 *
 * :returns: conversation fetch by ids command
 */
const fetchByIds: ICommandCreator<Commands.IConversationFetchByIds> = (
    conversations: number[],
): Commands.IConversationFetchByIds => ({
    _aggregate: _aggregates.CONVERSATIONS,
    _name: _names.CONVERSATION_FETCH_BY_IDS,
    _target: null,
    conversations,
});


/**
 * I am a command creator for fetch message by original id.
 *
 * :param message: message id
 * :param reply: original id of message being replied
 * :param conversation: conversation id of replying message
 *
 * :returns: fetch message by original id command
 */
const fetchMessageByOriginalId: ICommandCreator<
    Commands.IConversationFetchMessageByOriginalId
> = (
    message: number,
    reply: string,
    conversation: number,
): Commands.IConversationFetchMessageByOriginalId => ({
    _aggregate: _aggregates.CONVERSATIONS,
    _name: _names.CONVERSATION_FETCH_MESSAGE_BY_ORIGINAL_ID,
    _target: conversation,
    message,
    reply,
});


/**
 * I am a command creator for conversation fetch metrics.
 *
 * :param force: should fetch data anyway
 *
 * :returns: conversation fetch metrics command
 */
const fetchMetrics: ICommandCreator<
    Commands.IConversationFetchMetrics
> = (force: boolean): Commands.IConversationFetchMetrics => ({
    _aggregate: _aggregates.CONVERSATIONS,
    _name: _names.CONVERSATION_FETCH_METRICS,
    _target: null,
    force,
});


/**
 * I am a command creator for quality report.
 *
 * :returns: quality report command
 */
function fetchQualityReport (): Commands.IConversationFetchQualityReport
{
    return {
        _aggregate: _aggregates.CONVERSATIONS,
        _name: _names.CONVERSATION_FETCH_QUALITY_REPORT,
        _target: null,
    };
}


/**
 * I am a command creator for real time metrics.
 *
 * :returns: real time metrics command
 */
function fetchRealTimeMetrics (): Commands.IConversationFetchRealTimeMetrics
{
    return {
        _aggregate: _aggregates.CONVERSATIONS,
        _name: _names.CONVERSATION_FETCH_REALTIME_METRICS,
        _target: null,
    };
}


/**
 * I am a command creator for conversation finish.
 *
 * :param conversations: conversations ids
 *
 * :returns: conversation finish command
 */
function finish (conversations: number[]): Commands.IConversationFinish
{
    return {
        _aggregate: _aggregates.CONVERSATIONS,
        _name: _names.CONVERSATION_FINISH,
        _target: null,
        conversations,
    };
}


/**
 * I am a command creator for conversation focus.
 *
 * :param account: account uid
 * :param contact: contact uid
 * :param conversation: conversation id
 *
 * :returns: conversation focus command
 */
function focus (
    account: number | null,
    contact: string | number | null,
    conversation: string | number | null,
): Commands.IConversationFocus
{
    return {
        _aggregate: _aggregates.CONVERSATIONS,
        _name: _names.CONVERSATION_FOCUS,
        _target: (typeof conversation === 'number') ? conversation : null,
        account,
        contact,
        conversation,
    };
}


/**
 * I am a command creator for conversation force finish.
 *
 * :param conversation: conversation id
 *
 * :returns: conversation force finish command
 */
function forceFinish (
    conversation: number,
): Commands.IConversationForceFinish
{
    return {
        _aggregate: _aggregates.CONVERSATIONS,
        _name: _names.CONVERSATION_FORCE_FINISH_BY_ADMIN,
        _target: conversation,
    };
}


/**
 * I am a command creator for conversation listen.
 *
 * :returns: conversation listen command
 */
function listen (): Commands.IConversationListen
{
    return {
        _aggregate: _aggregates.CONVERSATIONS,
        _name: _names.CONVERSATION_LISTEN,
        _target: null,
    };
}


/**
 * I am a command creator for conversation load.
 *
 * :param agent: agent id
 *
 * :returns: conversation load command
 */
const load: ICommandCreator<Commands.IConversationLoad> =
    (agent: string): Commands.IConversationLoad => ({
        _aggregate: _aggregates.CONVERSATIONS,
        _name: _names.CONVERSATION_LOAD,
        _target: null,
        agent,
    });


/**
 * I am a command creator for conversation load by customer.
 *
 * :param customer: customer id
 *
 * :returns: conversation load by customer command
 */
function loadByCustomer (
    customer: number | string,
): Commands.IConversationLoadByCustomer
{
    return {
        _aggregate: _aggregates.CONVERSATIONS,
        _name: _names.CONVERSATION_LOAD_BY_CUSTOMER,
        _target: null,
        customer,
    };
}


/**
 * I am a command creator for conversation load by period.
 *
 * :param agent: agent id
 * :param from: period start timestamp in unix seconds
 * :param to: period end timestamp in unix seconds
 *
 * :returns: conversation load by period command
 */
function loadByPeriod (
    agent: string,
    from: number,
    to: number,
): Commands.IConversationLoadByPeriod
{
    return {
        _aggregate: _aggregates.CONVERSATIONS,
        _name: _names.CONVERSATION_LOAD_BY_PERIOD,
        _target: null,
        agent,
        from,
        to,
    };
}


/**
 * I am a command creator for load ongoing conversations by customer.
 *
 * :param customer: customer id
 *
 * :returns: command attributes
 */
function loadOngoingByCustomer (
    customer: string | number,
): Commands.IConversationLoadOngoingByCustomer
{
    return {
        _aggregate: _aggregates.CONVERSATIONS,
        _name: _names.CONVERSATION_LOAD_ONGOING_BY_CUSTOMER,
        _target: null,
        customer,
    };
}


/**
 * I am a command creator for online agents load.
 *
 * :param company: company id
 *
 * :returns: online agents load command
 */
function loadOnlineAgents (
    company: number,
): Commands.IConversationLoadOnlineAgents
{
    return {
        _aggregate: _aggregates.CONVERSATIONS,
        _name: _names.CONVERSATION_LOAD_ONLINE_AGENTS,
        _target: null,
        company,
    };
}

/**
 * I am a command creator for conversation load page.
 *
 * :returns: conversation load page command
 */
function loadPage (): Commands.IConversationLoadPage
{
    return {
        _aggregate: _aggregates.CONVERSATIONS,
        _name: _names.CONVERSATION_LOAD_PAGE,
        _target: null,
    };
}


/**
 * I am a command creator for conversation prioritize order.
 *
 * :param addHistoryBottom: ids to be added to the bottom of history
 * :param addHistoryTop: ids to be added to the top of history
 * :param addOngoingBottom: ids to be added to the bottom of ongoing
 * :param addOngoingTop: ids to be added to the top of ongoing
 * :param remove: ids to be removed
 *
 * :returns: conversation move to top command
 */
function prioritizeOrder (
    addHistoryBottom: (number | string)[],
    addHistoryTop: (number | string)[],
    addOngoingBottom: (number | string)[],
    addOngoingTop: (number | string)[],
    remove: (number | string)[],
): Commands.IConversationPrioritizeOrder
{
    return {
        _aggregate: _aggregates.CONVERSATIONS,
        _name: _names.CONVERSATION_PRIORITIZE_ORDER,
        _target: null,
        addHistoryBottom,
        addHistoryTop,
        addOngoingBottom,
        addOngoingTop,
        remove,
    };
}


/**
 * I am a command creator for conversation on create.
 *
 * :param account: conversation account id
 * :param channel: conversation channel
 * :param contactId: contact id
 * :param contactUid: contact uid
 * :param hasAgentAnswer: has agent answer
 * :param id: conversation id
 * :param name: contact name
 * :param lastInteraction: conversation last interaction timestamp
 * :param start: conversation start timestamp
 * :param subject: conversation subject
 * :param unansweredMessages: conversation amount of unanswered messages
 *
 * :returns: conversation on create command
 */
const onCreate: ICommandCreator<Commands.IConversationOnCreate> = (
    account: number,
    channel: string,
    contactId: number,
    contactUid: string,
    hasAgentAnswer: boolean | null,
    id: number,
    name: string,
    lastInteraction: number,
    start: number,
    subject: string | null,
    templateRequired: boolean,
    templateSent: number | null,
    unansweredMessages: number,
): Commands.IConversationOnCreate => ({
    _aggregate: _aggregates.CONVERSATIONS,
    _name: _names.CONVERSATION_ON_CREATE,
    _target: id,
    account,
    channel,
    contactId,
    contactUid,
    hasAgentAnswer,
    lastInteraction,
    name,
    start,
    subject,
    templateRequired,
    templateSent,
    unansweredMessages,
});


/**
 * I am a command creator for echo conversation creation.
 *
 * :param account: account id
 * :param channel: conversation channel
 * :param contactId: contact id
 * :param contactUid: contact uid
 * :param hasAgentAnswer: has agent answer
 * :param id: conversation id
 * :param name: contact name
 * :param lastInteraction: last interaction timestamp
 * :param start: start timestamp
 * :param subject: conversation subject
 * :param templateRequired: is template required
 * :param templateSent: template sent timestamp
 * :param unansweredMessages: number of unanswered messages
 *
 * :returns: echo conversation create command
 */
const onEchoCreate: ICommandCreator<Commands.IConversationOnEchoCreate> = (
    account: number,
    channel: string,
    contactId: number,
    contactUid: string,
    hasAgentAnswer: boolean | null,
    id: number,
    name: string,
    lastInteraction: number,
    start: number,
    subject: string | null,
    templateRequired: boolean,
    templateSent: number | null,
    unansweredMessages: number,
): Commands.IConversationOnEchoCreate => ({
    _aggregate: _aggregates.CONVERSATIONS,
    _name: _names.CONVERSATION_ON_ECHO_CREATE,
    _target: id,
    account,
    channel,
    contactId,
    contactUid,
    hasAgentAnswer,
    lastInteraction,
    name,
    start,
    subject,
    templateRequired,
    templateSent,
    unansweredMessages,
});


/**
 * I am a command creator for on echo message.
 *
 * :param ack: message ack type
 * :param body: message body
 * :param caption: message caption
 * :param conversation: message conversation's id
 * :param id: message id
 * :param messageClass: message class
 * :param messageType: message type
 * :param mode: message mode
 * :param name: message name
 * :param originalId: message original id
 * :param outgoing: message outgoing
 * :param referral: message referral
 * :param reply: original id of message being replied
 * :param timestamp: message timestamp
 * :param uid: message contact's uid
 * :param unansweredMessages: number of unanswered messages or nothing
 * :param outgoing: message outgoing
 *
 * :returns: events
 */
function onEchoMessage (
    ack: string,
    body: string,
    caption: string,
    conversation: number,
    id: number,
    messageClass: string,
    messageType: number,
    mode: number | null,
    name: string,
    originalId: string,
    referral: string | null,
    reply: string | null,
    timestamp: number,
    uid: string,
    unansweredMessages: number | null,
    outgoing: string | null,
): Commands.IConversationOnEchoMessage
{
    return {
        _aggregate: _aggregates.CONVERSATIONS,
        _name: _names.CONVERSATION_ON_ECHO_MESSAGE,
        _target: conversation,
        ack,
        body,
        caption,
        id,
        messageClass,
        messageType,
        mode,
        name,
        originalId,
        outgoing,
        referral,
        reply,
        timestamp,
        uid,
        unansweredMessages,
    };
}


/**
 * I am a command creator for conversation on exit.
 *
 * :param id: conversation id
 * :param status: conversation exit status
 * :param end: conversation end timestamp
 * :param lastInteraction: conversation last interaction timestamp
 * :param uid: conversation current app uid
 *
 * :returns: conversation on exit command
 */
const onExit: ICommandCreator<Commands.IConversationOnExit> = (
    id: number,
    status: number,
    end: number | null,
    lastInteraction: number,
    uid: string,
): Commands.IConversationOnExit => ({
    _aggregate: _aggregates.CONVERSATIONS,
    _name: _names.CONVERSATION_ON_EXIT,
    _target: id,
    end,
    lastInteraction,
    status,
    uid,
});


/**
 * I am a command creator for conversation on feedback.
 *
 * :param closable: error toast is closable or not
 * :param code: error code
 * :param duration: toast duration
 * :param params: error message params
 * :param title: error toast title
 * :param type: error toast type
 *
 * :returns: conversation on feedback command
 */
const onFeedback: ICommandCreator<Commands.IConversationOnFeedback> = (
    closable: boolean,
    code: string,
    duration: number,
    params: IFeedbackParams,
    title: string,
    type: ToastTypes,
): Commands.IConversationOnFeedback => ({
    _aggregate: _aggregates.CONVERSATIONS,
    _name: _names.CONVERSATION_ON_FEEDBACK,
    _target: null,
    closable,
    code,
    duration,
    params,
    title,
    type,
});


/**
 * I am a command creator for conversation on heartbeat conflict.
 *
 * :returns: conversation on forced session end command
 */
function onHeartbeatConflict (): Commands.IConversationOnHeartbeatConflict
{
    return {
        _aggregate: _aggregates.CONVERSATIONS,
        _name: _names.CONVERSATION_ON_HEARTBEAT_CONFLICT,
        _target: null,
    };
}


/**
 * I am a command creator for conversation on heartbeat success.
 *
 * :returns: conversation on login command
 */
function onHeartbeatSuccess (
    conversationsOnQueue: number,
): Commands.IConversationOnHeartbeatSuccess
{
    return {
        _aggregate: _aggregates.CONVERSATIONS,
        _name: _names.CONVERSATION_ON_HEARTBEAT_SUCCESS,
        _target: null,
        conversationsOnQueue: conversationsOnQueue,
    };
}


/**
 * I am a command creator for conversation on heartbeat failure.
 *
 * :returns: conversation on logout command
 */
function onHeartbeatFailure (): Commands.IConversationOnHeartbeatFailure
{
    return {
        _aggregate: _aggregates.CONVERSATIONS,
        _name: _names.CONVERSATION_ON_HEARTBEAT_FAILURE,
        _target: null,
    };
}


/**
 * I am a command creator for on message.
 *
 * :param ack: message ack type
 * :param body: message body
 * :param caption: message caption
 * :param conversation: message conversation's id
 * :param id: message id
 * :param messageClass: message class
 * :param messageType: message type
 * :param mode: message mode
 * :param name: message name
 * :param originalId: message original id
 * :param referral: message referral
 * :param reply: original id of message being replied
 * :param timestamp: message timestamp
 * :param uid: message contact's uid
 * :param unansweredMessages: number of unanswered messages or nothing
 *
 * :returns: events
 */
function onMessage (ack: string,
                    body: string,
                    caption: string,
                    conversation: number,
                    id: number,
                    messageClass: string,
                    messageType: number,
                    mode: number | null,
                    name: string,
                    originalId: string,
                    referral: string | null,
                    reply: string | null,
                    timestamp: number,
                    uid: string,
                    unansweredMessages: number | null,
): Commands.IConversationOnMessage
{
    return {
        _aggregate: _aggregates.CONVERSATIONS,
        _name: _names.CONVERSATION_ON_MESSAGE,
        _target: conversation,
        ack,
        body,
        caption,
        id,
        messageClass,
        messageType,
        mode,
        name,
        originalId,
        referral,
        reply,
        timestamp,
        uid,
        unansweredMessages,
    };
}


/**
 * I am a command creator for on message ack.
 *
 * :param conversationId: conversation id
 * :param messageId: message id
 * :param ack: message ack
 * :param originalId: message originalId
 *
 * :returns: conversation on message ack command
 */
function onMessageAck (
    conversationId: number,
    messageId: number,
    ack: AckTypes,
    originalId: string,
): Commands.IConversationOnMessageAck
{
    return {
        _aggregate: _aggregates.CONVERSATIONS,
        _name: _names.CONVERSATION_ON_MESSAGE_ACK,
        _target: conversationId,
        ack,
        id: messageId,
        originalId,
    };
}


/**
 * I am a command creator for conversation open session.
 *
 * :returns: conversation open session command
 */
function openSession (): Commands.IConversationOpenSession
{
    return {
        _aggregate: _aggregates.CONVERSATIONS,
        _name: _names.CONVERSATION_OPEN_SESSION,
        _target: null,
    };
}


/**
 * I am a command creator for conversation remove.
 *
 * :param id: conversation id
 *
 * :returns: conversation remove command
 */
const remove: ICommandCreator<Commands.IConversationRemove> =
    (id: number): Commands.IConversationRemove => ({
        _aggregate: _aggregates.CONVERSATIONS,
        _name: _names.CONVERSATION_REMOVE,
        _target: id,
    });


/**
 * I am a command creator for conversation remove feedback.
 *
 * :param index: feedback index
 *
 * :returns: conversation remove feedback command
 */
const removeFeedback: ICommandCreator<Commands.IConversationRemoveFeedback> =
    (index: number): Commands.IConversationRemoveFeedback => ({
        _aggregate: _aggregates.CONVERSATIONS,
        _name: _names.CONVERSATION_REMOVE_FEEDBACK,
        _target: null,
        index,
    });


/**
 * I am a command creator for conversation remove forwarding message.
 *
 * :param message: message id to remove
 *
 * :returns: conversation remove forwarding message command
 */
function removeForwardingMessage (
    message: number,
): Commands.IConversationRemoveForwardingMessage
{
    return {
        _aggregate: _aggregates.CONVERSATIONS,
        _name: _names.CONVERSATION_REMOVE_FORWARDING_MESSAGE,
        _target: null,
        message,
    };
}


/**
 * I am a command creator for conversation reset error.
 *
 * :returns: conversation reset error command
 */
const resetError: ICommandCreator<Commands.IConversationResetError> =
    (): Commands.IConversationResetError => ({
        _aggregate: _aggregates.CONVERSATIONS,
        _name: _names.CONVERSATION_RESET_ERROR,
        _target: null,
    });


/**
 * I am a command creator for conversations transfer reset.
 *
 * :returns: conversation transfer reset command
 */
function resetTransfer (): Commands.IConversationResetTransfer
{
    return {
        _aggregate: _aggregates.CONVERSATIONS,
        _name: _names.CONVERSATION_RESET_TRANSFER,
        _target: null,
    };
}


/**
 * I am a command creator for conversation retry message.
 *
 * :param id: conversation id
 * :param message: message to be sent
 * :param onProgress: progress upload callback
 * :param onSuccess: successful upload callback
 *
 * :returns: conversation retry message command
 */
const retryMessage = (
    id: number,
    message: IMessage,
    onProgress: IMessageOnProgress,
    onSuccess: IMessageOnSuccess,
): IConversationRetryMessage => ({
    _aggregate: _aggregates.CONVERSATIONS,
    _name: _names.CONVERSATION_RETRY_MESSAGE,
    _target: id,
    message,
    onProgress,
    onSuccess,
});


/**
 * I am a command creator for conversation send email.
 *
 * :param account: account id
 * :param attachments: email attachments
 * :param body: email body
 * :param contact: contact uid
 * :param conversation: conversation id or nothing
 * :param onProgress: progress upload callback
 * :param onSuccess: successful upload callback
 * :param sender: user that sent the message
 * :param subject: conversation subject
 *
 * :returns: conversation send message command
 */
function sendEmail (
    account: number,
    attachments: File[],
    body: string,
    contact: string,
    conversation: number | null,
    onProgress: IMessageOnProgress,
    onSuccess: IMessageOnSuccess,
    sender: string,
    subject: string,
): Commands.IConversationSendEmail
{
    return {
        _aggregate: _aggregates.CONVERSATIONS,
        _name: _names.CONVERSATION_SEND_EMAIL,
        _target: conversation,
        account,
        attachments,
        body,
        contact,
        onProgress,
        onSuccess,
        sender,
        subject,
    };
}


/**
 * I am a command creator for conversation send message.
 *
 * :param contact: contact uid
 * :param conversation: conversation id
 * :param message: message to be sent
 * :param onProgress: progress upload callback
 * :param onSuccess: successful upload callback
 *
 * :returns: conversation send message command
 */
const sendMessage: ICommandCreator<Commands.IConversationSendMessage> = (
    contact: string,
    conversation: number | null,
    message: IMessage,
    uploaded,
    onProgress: IMessageOnProgress,
    onSuccess: IMessageOnSuccess,
): Commands.IConversationSendMessage => ({
    _aggregate: _aggregates.CONVERSATIONS,
    _name: _names.CONVERSATION_SEND_MESSAGE,
    _target: conversation,
    contact,
    message,
    onProgress,
    onSuccess,
    uploaded,
});


/**
 *  I am a command creator for a conversation send template.
 *
 * :param id: conversation id
 * :param templateInput: template data input for conversis
 *
 * :returns: conversation send template command
 */
const sendTemplate: ICommandCreator<Commands.IConversationSendTemplate> = (
    id: number,
    templateInput: ITemplateInput,
): Commands.IConversationSendTemplate => ({
    _aggregate: _aggregates.CONVERSATIONS,
    _name: _names.CONVERSATION_SEND_TEMPLATE,
    _target: id,
    templateInput,
});


/**
 * I am a command creator for set metrics period.
 *
 * :param endDate: final timestamp
 * :param startDate: initial timestamp
 * :param filters: metrics filters
 *
 * :returns: conversation set metrics period command
 */
function setMetricsPeriod (
    endDate: number,
    startDate: number,
    filters?: IMetricsFilters,
): Commands.IConversationSetMetricsPeriod
{
    return {
        _aggregate: _aggregates.CONVERSATIONS,
        _name: _names.CONVERSATION_SET_METRICS_PERIOD,
        _target: null,
        endDate,
        filters,
        startDate,
    };
}


/**
 * I am a command creator to dispatch the quality analysis of the loaded
 * conversations without a score.
 *
 * :returns: analyze quality command
 */
function analyzeQuality (): Commands.IConversationAnalyzeQuality
{
    return {
        _aggregate: _aggregates.CONVERSATIONS,
        _name: _names.CONVERSATION_ANALYZE_QUALITY,
        _target: null,
    };
}


/**
 * I am a command creator to expire dispatched analyses that got no event.
 *
 * :param conversations: dispatched conversation ids
 *
 * :returns: expire quality analyses command
 */
function expireQualityAnalyses (
    conversations: number[],
): Commands.IConversationExpireQualityAnalyses
{
    return {
        _aggregate: _aggregates.CONVERSATIONS,
        _name: _names.CONVERSATION_EXPIRE_QUALITY_ANALYSES,
        _target: null,
        conversations,
    };
}


/**
 * I am a command creator for a conversation-analyzed event received from
 * the subscription.
 *
 * :param analyzedAt: analysis timestamp
 * :param conversation: conversation id
 * :param failed: analysis failed
 * :param score: score, null when failed
 *
 * :returns: on quality analyzed command
 */
function onQualityAnalyzed (
    analyzedAt: number,
    conversation: number,
    failed: boolean,
    score: number | null,
): Commands.IConversationOnQualityAnalyzed
{
    return {
        _aggregate: _aggregates.CONVERSATIONS,
        _name: _names.CONVERSATION_ON_QUALITY_ANALYZED,
        _target: conversation,
        analyzedAt,
        conversation,
        failed,
        score,
    };
}


/**
 * I am a command creator to leave the quality report screen.
 *
 * :returns: stop quality report command
 */
function stopQualityReport (): Commands.IConversationStopQualityReport
{
    return {
        _aggregate: _aggregates.CONVERSATIONS,
        _name: _names.CONVERSATION_STOP_QUALITY_REPORT,
        _target: null,
    };
}


/**
 * I am a command creator for set quality report metadata.
 *
 * :param metadata: quality report metadata
 *
 * :returns: conversation set quality report metadata command
 */
function setQualityMetadata (
    metadata: IQualityMetadata,
): Commands.IConversationSetQualityMetadata
{
    return {
        _aggregate: _aggregates.CONVERSATIONS,
        _name: _names.CONVERSATION_SET_QUALITY_METADATA,
        _target: null,
        metadata,
    };
}


/**
 * I am a command creator for conversation start.
 *
 * :param isAdmin: whether the user is and administrator
 *
 * :returns: conversation start command
 */
const start: ICommandCreator<Commands.IConversationStart> =
    (isAdmin: boolean): Commands.IConversationStart => ({
        _aggregate: _aggregates.CONVERSATIONS,
        _name: _names.CONVERSATION_START,
        _target: null,
        isAdmin,
    });


/**
 * I am a command creator for conversation start forward.
 *
 * :returns: conversation start forward command
 */
function startForward (): Commands.IConversationStartForward
{
    return {
        _aggregate: _aggregates.CONVERSATIONS,
        _name: _names.CONVERSATION_START_FORWARD,
        _target: null,
    };
}


/**
 * I am a command creator for conversation stop.
 *
 * :param isAdmin: whether the user is an administrator
 *
 * :returns: conversation stop command
 */
const stop: ICommandCreator<Commands.IConversationStop> =
    (isAdmin: boolean): Commands.IConversationStop => ({
        _aggregate: _aggregates.CONVERSATIONS,
        _name: _names.CONVERSATION_STOP,
        _target: null,
        isAdmin,
    });


/**
 * I am a command creator for conversation stop forward.
 *
 * :returns: conversation stop forward command
 */
function stopForward (): Commands.IConversationStopForward
{
    return {
        _aggregate: _aggregates.CONVERSATIONS,
        _name: _names.CONVERSATION_STOP_FORWARD,
        _target: null,
    };
}


/**
 * I am a command creator for conversation transfer.
 *
 * :param id: conversation id
 * :param to: receiver agent uid
 *
 * :returns: conversation transfer command
 */
function transfer (id: number, to: string): Commands.IConversationTransfer
{
    return {
        _aggregate: _aggregates.CONVERSATIONS,
        _name: _names.CONVERSATION_TRANSFER,
        _target: id,
        to,
    };
}


/**
 * I am a command creator for type text.
 *
 * :param id: conversation id
 * :param text: typed message text
 *
 * :returns: conversation on type text command
 */
function onTypeText (
    id: number,
    text: string,
): Commands.IConversationOnTypeText
{
    return {
        _aggregate: _aggregates.CONVERSATIONS,
        _name: _names.CONVERSATION_ON_TYPE_TEXT,
        _target: id,
        text,
    };
}


/**
 * I am a command creator for file upload progress.
 *
 * :param id: message id
 * :param originalId: message original id
 * :param percentage: percentage of upload progress
 *
 * :returns: conversation upload progress command
 */
function onUploadProgress (id: number,
                           originalId: string,
                           percentage: number): IConversationOnUploadProgress
{
    return {
        _aggregate: _aggregates.CONVERSATIONS,
        _name: _names.CONVERSATION_ON_UPLOAD_PROGRESS,
        _target: null,
        id,
        originalId,
        percentage,
    };
}


/**
 * I am a command creator for file upload success.
 *
 * :param id: message id
 * :param originalId: message original id
 *
 * :returns: conversation upload success command
 */
function onUploadSuccess (id: number,
                          originalId: string): IConversationOnUploadSuccess
{
    return {
        _aggregate: _aggregates.CONVERSATIONS,
        _name: _names.CONVERSATION_ON_UPLOAD_SUCCESS,
        _target: null,
        id,
        originalId,
    };
}


/**
 * EXPORTS
 */
export {
    addForwardingMessage,
    analyzeQuality,
    closeSession,
    create,
    ensure,
    ensureOngoing,
    expireQualityAnalyses,
    fetch,
    fetchAgentsMessagesMetrics,
    fetchByIds,
    fetchMessageByOriginalId,
    fetchMetrics,
    fetchQualityReport,
    finish,
    focus,
    forceFinish,
    fetchRealTimeMetrics,
    listen,
    load,
    loadByCustomer,
    loadByPeriod,
    loadOngoingByCustomer,
    loadOnlineAgents,
    loadPage,
    onQualityAnalyzed,
    prioritizeOrder,
    onCreate,
    onEchoCreate,
    onEchoMessage,
    onExit,
    onFeedback,
    onHeartbeatConflict,
    onHeartbeatFailure,
    onHeartbeatSuccess,
    onMessage,
    onMessageAck,
    onTypeText,
    onUploadProgress,
    onUploadSuccess,
    openSession,
    remove,
    removeFeedback,
    removeForwardingMessage,
    resetError,
    resetTransfer,
    retryMessage,
    sendEmail,
    sendMessage,
    sendTemplate,
    setMetricsPeriod,
    setQualityMetadata,
    start,
    startForward,
    stop,
    stopForward,
    stopQualityReport,
    transfer,
};
